import { observer } from 'mobx-react'
import React from 'react'
import { EditUserInformationDialogViewModel } from './EditUserInformationDialogViewModel'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '../../../components/ModalSheet'
import Button from '../../../components/Button'
import {
  FormColumn,
  FormColumns,
  FormGroupContainer,
  FormRow,
  TextInputContainer,
} from '../../../components/inputs'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from '../../../components/inputs/CustomSelect'
import Icon from '../../../components/Icon'
import styles from '../../../jobs/views/JobRoot/Job/AmendmentsCard/CreateAmendmentDialog/CreateAmendment.sass'

interface Props {
  vm: EditUserInformationDialogViewModel
}

const EditUserInformationDialog = observer(({ vm }: Props) => {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.userTypeOptions, (item, text) => {
        return item.title.toLowerCase().includes(text.toLowerCase())
      }),
    [vm.userTypeOptions],
  )
  const selectProps = useCustomSelectStateFromItemSource({ itemSource })

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton bordered>
        <ModalSheetTitle>Edit user information</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetSection spaced>
          <form onSubmit={vm.submit}>
            <FormRow>
              <FormColumns>
                <FormColumn>
                  <TextInputContainer
                    autoFocus
                    label="First Name"
                    field="givenName"
                    model={vm}
                    error={vm.validation.getError('givenName')}
                    data-test="given-name-input"
                  />
                </FormColumn>

                <FormColumn>
                  <TextInputContainer
                    autoFocus
                    label="Last Name"
                    field="familyName"
                    model={vm}
                    error={vm.validation.getError('familyName')}
                    data-test="family-name-input"
                  />
                </FormColumn>
              </FormColumns>
            </FormRow>

            <FormRow>
              <TextInputContainer
                autoFocus
                label="Email"
                field="email"
                model={vm}
                error={vm.validation.getError('email')}
                data-test="email-input"
              />
            </FormRow>

            <FormRow>
              <FormColumns>
                <FormColumn>
                  <TextInputContainer
                    autoFocus
                    label="Phone number"
                    field="phone"
                    data-test="phone-input"
                    model={vm}
                    error={vm.validation.getError('phone')}
                  />
                </FormColumn>

                {vm.isPlatformAdmin && (
                  <FormColumn>
                    <FormGroupContainer
                      model={vm}
                      label="User type"
                      field={'selectedUserType'}
                    >
                      <CustomSelect
                        {...selectProps}
                        multi={false}
                        value={vm.selectedUserType}
                        itemToString={(i) => i.title}
                        icon={
                          <Icon
                            size={24}
                            name={'dropdown'}
                            className={styles.icon}
                          />
                        }
                        data-test="edit-user-type-select"
                        onChange={vm.setUserType}
                      />
                    </FormGroupContainer>
                  </FormColumn>
                )}
              </FormColumns>
            </FormRow>
          </form>
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Cancel
          </Button>
          <Button
            submit
            type="primary"
            data-test="edit-user-information-save-button"
            onClick={vm.submit}
            loading={vm.submit.pending}
          >
            Save
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

export default EditUserInformationDialog
